import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import "./Pagination.scss";

export type PaginationProps = {
  /** 1-based */
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
  /** With both, shows "Showing 1–10 of 54" */
  totalItems?: number;
  pageSize?: number;
  className?: string;
};

// 1 … 4 5 [6] 7 8 … 20
const pageList = (page: number, total: number): (number | "gap")[] => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, page - 1, page, page + 1]);
  if (page <= 3) [2, 3, 4].forEach((p) => pages.add(p));
  if (page >= total - 2) [total - 3, total - 2, total - 1].forEach((p) => pages.add(p));
  const sorted = Array.from(pages).filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out: (number | "gap")[] = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) out.push("gap");
    out.push(p);
  });
  return out;
};

/** Page numbers with previous / next, and the range shown. */
export function Pagination({ page, totalPages, onChange, totalItems, pageSize, className }: PaginationProps): React.JSX.Element | null {
  const showRange = totalItems !== undefined && pageSize !== undefined;
  if (totalPages <= 1 && !showRange) return null;
  const from = showRange ? (totalItems === 0 ? 0 : (page - 1) * pageSize! + 1) : 0;
  const to = showRange ? Math.min(page * pageSize!, totalItems!) : 0;

  return (
    <nav className={["ui-pagination", className].filter(Boolean).join(" ")} aria-label="Pagination">
      {showRange ? (
        <p className="ui-pagination__range">
          Showing <b>{from}</b>–<b>{to}</b> of <b>{totalItems}</b>
        </p>
      ) : (
        <span />
      )}
      {totalPages > 1 ? (
        <div className="ui-pagination__pages">
          <button type="button" className="ui-pagination__btn" onClick={() => onChange(page - 1)} disabled={page <= 1} aria-label="Previous page">
            <ChevronLeft size={16} />
          </button>
          {pageList(page, totalPages).map((p, i) =>
            p === "gap" ? (
              <span key={`gap-${i}`} className="ui-pagination__gap">
                …
              </span>
            ) : (
              <button
                key={p}
                type="button"
                className={`ui-pagination__btn${p === page ? " is-current" : ""}`}
                aria-current={p === page ? "page" : undefined}
                onClick={() => p !== page && onChange(p)}
              >
                {p}
              </button>
            )
          )}
          <button type="button" className="ui-pagination__btn" onClick={() => onChange(page + 1)} disabled={page >= totalPages} aria-label="Next page">
            <ChevronRight size={16} />
          </button>
        </div>
      ) : null}
    </nav>
  );
}

export default Pagination;
